import * as React from 'react';
import { useNavigate, useLocation, createSearchParams } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import SearchIcon from '@mui/icons-material/Search';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import ClearIcon from '@mui/icons-material/Close';

import { useApp } from '../../../context';
import { getLocations } from '../../../utils/platformConfig';
import { searchListings } from '../../../utils/search';
import { LOCATIONS as CODE_LOCATIONS } from '../../../data/locations';

/** Sentinel for "don't filter by location". Empty string in the URL. */
export const ALL_LOCATIONS = '';

/** How many listings the dropdown offers before deferring to the map view. */
const MAX_SUGGESTIONS = 6;

/** The trailing "see everything" row. Marked, not guessed at by shape. */
const SEE_ALL = '__see_all__';

/**
 * The universal search bar in the nav.
 *
 * Searches listings three ways, and they compose: free text across every
 * location, one chosen location, or a name within a location. The text and the
 * location are separate inputs on purpose — typing "Kariba" as free text is a
 * guess about what the traveller means, whereas picking it from the menu is a
 * statement, and only the second can be trusted to filter by the exact
 * canonical value `listings.location` is stored as.
 *
 * TWO WAYS OUT, and they answer different questions:
 *
 *   pick a suggestion  → straight to that listing. "I know what I want."
 *   press Enter        → /search, the map view. "Show me what there is."
 *
 * Built on MUI's Autocomplete rather than a hand-rolled popper so the listbox
 * comes with arrow-key navigation, type-ahead focus management, Escape-to-close
 * and the aria wiring already correct — none of which is visible when it works
 * and all of which is a bug report when it does not.
 *
 * State lives in the URL so a result set can be linked to, refreshed, and
 * reached with the Back button — the mistake the destination page had to be
 * rescued from when it read its subject from router state.
 */
export default function UniversalSearch({ autoFocus = false, onSubmitted, sx }) {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { serviceListing } = useApp();

  const [locations, setLocations] = React.useState(CODE_LOCATIONS);
  const [anchorEl, setAnchorEl] = React.useState(null);

  const params = React.useMemo(
    () => new URLSearchParams(routerLocation.search),
    [routerLocation.search]
  );

  // Seeded FROM THE URL, so landing on /search?q=… shows what is being shown,
  // and editing continues from there rather than from an empty box.
  const [query, setQuery] = React.useState(() => params.get('q') ?? '');
  const [place, setPlace] = React.useState(() => params.get('location') ?? ALL_LOCATIONS);

  // Re-sync when the URL changes underneath us — a second search from the
  // results page, or the Back button, must not leave stale text in the box.
  React.useEffect(() => {
    setQuery(params.get('q') ?? '');
    setPlace(params.get('location') ?? ALL_LOCATIONS);
  }, [params]);

  React.useEffect(() => {
    let live = true;
    getLocations().then((next) => {
      if (live && next?.length) setLocations(next);
    });
    return () => { live = false; };
  }, []);

  const selected = locations.find((l) => l.value === place);
  const placeLabel = selected ? (selected.label || selected.value) : 'All locations';

  /**
   * What the dropdown offers.
   *
   * The SAME `searchListings` the results page runs, so the suggestions are a
   * prefix of the results rather than a second, subtly different search — a
   * suggestion the map then fails to show is worse than no suggestion.
   *
   * Nothing is offered for an empty box with no location: a list of every
   * listing on the platform is not a suggestion, it is a catalogue.
   */
  const matches = React.useMemo(() => {
    if (!query.trim() && !place) return [];
    return searchListings(serviceListing, { query, location: place });
  }, [serviceListing, query, place]);

  const options = React.useMemo(() => {
    if (!matches.length) return [];
    // The overflow row is always last, and always present when there is
    // anything at all — "see them on the map" is a useful answer even when
    // every match already fits.
    return [...matches.slice(0, MAX_SUGGESTIONS), SEE_ALL];
  }, [matches]);

  const submit = (event) => {
    event?.preventDefault?.();

    const search = createSearchParams({
      // Only what was actually asked for: an empty `q=` in the URL is noise,
      // and it makes two identical searches look like different pages.
      ...(query.trim() ? { q: query.trim() } : {}),
      ...(place ? { location: place } : {}),
    }).toString();

    navigate({ pathname: '/search', search });
    onSubmitted?.();
  };

  /**
   * A choice from the listbox.
   *
   * `freeSolo` means this also fires with a raw string when someone presses
   * Enter without highlighting anything — which is the "show me everything"
   * path, not a selection.
   */
  const handleSelect = (event, value) => {
    if (!value || typeof value === 'string') {
      submit(event);
      return;
    }
    if (value === SEE_ALL) {
      submit(event);
      return;
    }
    navigate(`/listing/${value.id}`);
    onSubmitted?.();
  };

  const chooseLocation = (value) => {
    setPlace(value);
    setAnchorEl(null);
  };

  return (
    <Box
      component="form"
      role="search"
      onSubmit={submit}
      sx={{ width: '100%', maxWidth: 640, ...sx }}
    >
      <Autocomplete
        fullWidth
        freeSolo
        selectOnFocus
        handleHomeEndKeys
        clearOnBlur={false}
        options={options}
        // Already filtered, and by the same function the results page uses.
        // Autocomplete's own substring filter would narrow the list a second
        // time, on a different rule, and drop matches found on tags.
        filterOptions={(x) => x}
        inputValue={query}
        onInputChange={(event, value, reason) => {
          // 'reset' fires when a selection writes its label back into the box.
          // We navigate away on selection, so adopting it would only flash the
          // chosen listing's name into a field the traveller is leaving.
          if (reason !== 'reset') setQuery(value);
        }}
        onChange={handleSelect}
        getOptionLabel={(option) => {
          if (typeof option === 'string') return option === SEE_ALL ? query : option;
          return option.name ?? '';
        }}
        renderOption={(props, option) => {
          // MUI puts `key` in the spread props; React wants it passed directly.
          const { key, ...optionProps } = props;

          if (option === SEE_ALL) {
            return (
              <Box component="li" key={key} {...optionProps} sx={{ gap: 1 }}>
                <SearchIcon fontSize="small" sx={{ color: 'primary.main' }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  See all {matches.length} {matches.length === 1 ? 'result' : 'results'} on the map
                </Typography>
              </Box>
            );
          }

          return (
            <Box component="li" key={key} {...optionProps} sx={{ gap: 1, alignItems: 'flex-start' }}>
              <LocationOnRoundedIcon fontSize="small" sx={{ color: 'text.secondary', mt: 0.25 }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                  {option.name}
                </Typography>
                {/* Why this row matched, in the two fields a traveller is
                    choosing between when several listings share a name. */}
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {[option.location, option.type].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
            </Box>
          );
        }}
        slotProps={{
          paper: { sx: { mt: 1, borderRadius: 3, overflow: 'hidden' } },
          listbox: { sx: { py: 0, '& .MuiAutocomplete-option': { px: 1.5, py: 1 } } },
        }}
        renderInput={(params) => (
          <Box
            // MUI v9 hands the container ref and the input props through
            // `slotProps`; forwarding both is what keeps focus management and
            // arrow-key navigation working with a custom-shaped input.
            ref={params.slotProps.input.ref}
            onMouseDown={params.slotProps.input.onMouseDown}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              width: '100%',
              pl: 1.5,
              pr: 0.5,
              py: 0.25,
              borderRadius: 999,
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark'
                  ? alpha(theme.palette.common.white, 0.06)
                  : alpha(theme.palette.common.black, 0.04),
              transition: (theme) => theme.transitions.create(['border-color', 'box-shadow']),
              '&:focus-within': {
                borderColor: 'primary.main',
                boxShadow: (theme) => `0 0 0 3px ${alpha(theme.palette.primary.main, 0.18)}`,
              },
            }}
          >
            <SearchIcon fontSize="small" sx={{ color: 'text.secondary', flexShrink: 0 }} />

            <InputBase
              autoFocus={autoFocus}
              placeholder="Search experiences, stays, food..."
              inputProps={{ ...params.slotProps.htmlInput, 'aria-label': 'Search listings' }}
              sx={{ flex: 1, minWidth: 0, fontSize: 14, '& input::placeholder': { opacity: 0.7 } }}
            />

            {query && (
              <Tooltip title="Clear">
                <IconButton size="small" aria-label="Clear search text" onClick={() => setQuery('')}>
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}

            {/* The chosen location is SHOWN, not hidden behind the icon. A
                filter a traveller cannot see is one they cannot tell is why
                their search came back with three results. */}
            <Button
              size="small"
              onClick={(e) => setAnchorEl(e.currentTarget)}
              startIcon={<MyLocationIcon sx={{ fontSize: 18 }} />}
              aria-haspopup="listbox"
              aria-label={`Filter by location: ${placeLabel}`}
              sx={{
                flexShrink: 0,
                borderRadius: 999,
                minWidth: 0,
                px: selected ? 1.25 : 1,
                color: selected ? 'primary.main' : 'text.secondary',
                '& .MuiButton-startIcon': { mr: selected ? 0.5 : 0 },
              }}
            >
              {selected && (
                <Box component="span" sx={{ fontSize: 12, maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {placeLabel}
                </Box>
              )}
            </Button>

            <IconButton
              type="submit"
              size="small"
              aria-label="Search"
              sx={{
                flexShrink: 0,
                color: 'common.white',
                backgroundColor: 'primary.main',
                '&:hover': { backgroundColor: 'primary.dark' },
              }}
            >
              <SearchIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        )}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { sx: { maxHeight: 360, width: 260 } } }}
      >
        <MenuItem selected={!place} onClick={() => chooseLocation(ALL_LOCATIONS)}>
          <ListItemText primary="All locations" />
        </MenuItem>
        <Divider />
        {locations.map((l) => (
          <MenuItem key={l.value} selected={l.value === place} onClick={() => chooseLocation(l.value)}>
            <ListItemText primary={l.label || l.value} secondary={l.region} />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
